import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, Send, Check, Truck, TrendingUp, Clock, Users, 
  Loader2, Building2, User, Mail, Phone, Globe, Calendar, MapPin,
  CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countries = [
  "Morocco", "Germany", "France", "United Kingdom", "Netherlands", "Belgium", 
  "Spain", "Italy", "Portugal", "Austria", "Switzerland",
  "Poland", "Czech Republic", "Denmark", "Sweden", "Norway",
  "Finland", "Ireland", "Luxembourg", "Greece", "Hungary",
  "Other"
];

const TransporterRegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    country: '',
    fleet_size: '',
    experience_years: '',
    services_offered: [],
    coverage_areas: '',
    additional_info: ''
  });
  const [errors, setErrors] = useState({});

  // Translated options
  const fleetSizeOptions = [
    { value: "1-2 vehicles", labelKey: "transporter.fleetSizes.1-2" },
    { value: "3-5 vehicles", labelKey: "transporter.fleetSizes.3-5" },
    { value: "6-10 vehicles", labelKey: "transporter.fleetSizes.6-10" },
    { value: "11-20 vehicles", labelKey: "transporter.fleetSizes.11-20" },
    { value: "20+ vehicles", labelKey: "transporter.fleetSizes.20+" }
  ];

  const experienceOptions = [
    { value: "Less than 1 year", labelKey: "transporter.experienceYears.less1" },
    { value: "1-3 years", labelKey: "transporter.experienceYears.1-3" },
    { value: "3-5 years", labelKey: "transporter.experienceYears.3-5" },
    { value: "5-10 years", labelKey: "transporter.experienceYears.5-10" },
    { value: "10-20 years", labelKey: "transporter.experienceYears.10-20" },
    { value: "20+ years", labelKey: "transporter.experienceYears.20+" }
  ];

  const servicesOptions = [
    { id: "national", labelKey: "transporter.services.national" },
    { id: "international", labelKey: "transporter.services.international" },
    { id: "premium", labelKey: "transporter.services.premium" },
    { id: "shared", labelKey: "transporter.services.shared" },
    { id: "emergency", labelKey: "transporter.services.emergency" },
    { id: "competition", labelKey: "transporter.services.competition" },
    { id: "air", labelKey: "transporter.services.air" },
    { id: "customs", labelKey: "transporter.services.customs" }
  ];

  const whyJoinReasons = [
    { text: t('transporter.whyJoinReasons.reason1') },
    { text: t('transporter.whyJoinReasons.reason2') },
    { text: t('transporter.whyJoinReasons.reason3') },
    { text: t('transporter.whyJoinReasons.reason4') },
    { text: t('transporter.whyJoinReasons.reason5') }
  ];

  const benefitsData = [
    { icon: Truck, title: t('transporter.benefits.benefit1Title'), description: t('transporter.benefits.benefit1Desc') },
    { icon: TrendingUp, title: t('transporter.benefits.benefit2Title'), description: t('transporter.benefits.benefit2Desc') },
    { icon: Clock, title: t('transporter.benefits.benefit3Title'), description: t('transporter.benefits.benefit3Desc') },
    { icon: Users, title: t('transporter.benefits.benefit4Title'), description: t('transporter.benefits.benefit4Desc') }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = t('validation.companyNameRequired');
    if (!formData.contact_person.trim()) newErrors.contact_person = t('validation.contactPersonRequired');
    if (!formData.email.trim()) newErrors.email = t('validation.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('validation.invalidEmailFormat');
    if (!formData.phone.trim()) newErrors.phone = t('validation.phoneRequired');
    if (!formData.country) newErrors.country = t('validation.countryRequired');
    if (!formData.fleet_size) newErrors.fleet_size = t('validation.fleetSizeRequired');
    if (!formData.experience_years) newErrors.experience_years = t('validation.experienceRequired');
    if (formData.services_offered.length === 0) newErrors.services_offered = t('validation.servicesRequired');
    if (!formData.coverage_areas.trim()) newErrors.coverage_areas = t('validation.coverageRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => {
      const services = prev.services_offered.includes(serviceId)
        ? prev.services_offered.filter(s => s !== serviceId)
        : [...prev.services_offered, serviceId];
      return { ...prev, services_offered: services };
    });
    if (errors.services_offered) {
      setErrors(prev => ({ ...prev, services_offered: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/transporters/register`, formData);
      setSubmittedData(response.data);
      setIsSubmitted(true);
    } catch (err) {
      const message = err.response?.data?.detail || t('errors.submitFailed');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted && submittedData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="pt-32 pb-24 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">{t('transporter.registrationSuccess')}</h1>
            <p className="text-gray-400 text-lg mb-8">
              {t('transporter.welcomeToNetwork')}, <span className="text-amber-500">{submittedData.company_name}</span>!
            </p>
            
            {/* Check Email Box */}
            <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-500/30 rounded-2xl p-8 mb-8">
              <div className="text-5xl mb-4">✉️</div>
              <h3 className="text-white font-semibold text-xl mb-4">{t('transporter.checkYourEmail')}</h3>
              <p className="text-gray-300 mb-4">
                {t('transporter.credentialsSentTo')}
              </p>
              <p className="text-amber-500 font-mono text-lg font-bold bg-black/30 inline-block px-4 py-2 rounded-lg">
                {formData.email}
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 text-left">
              <h3 className="text-white font-semibold mb-4">{t('transporter.nextSteps')}</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
                  <span className="text-gray-300">{t('transporter.step1Web')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
                  <span className="text-gray-300">{t('transporter.step2Web')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
                  <span className="text-gray-300">{t('transporter.step3Web')}</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-500 text-sm mb-8">{t('transporter.checkSpam')}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/drivers/login')}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 rounded-full"
                data-testid="go-to-login-btn"
              >
                {t('common.goToLogin')}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-full"
                data-testid="back-to-home-btn"
              >
                {t('common.backToHome')}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-8"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.backToHome')}</span>
          </button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
                <Truck className="w-4 h-4 text-amber-500" />
                <span className="text-amber-500 text-sm font-medium">{t('transporter.portal')}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('transporter.joinNetwork')}
              </h1>

              <p className="text-gray-400 text-lg mb-8">
                {t('transporter.ordersWaiting')}
              </p>

              {/* Why Join */}
              <div className="space-y-3">
                <h3 className="text-white font-semibold mb-4">{t('transporter.whyJoin')}</h3>
                {whyJoinReasons.map((reason, index) => (
                  <div key={reason.text || `reason-${index}`} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span className="text-gray-300">{reason.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Login Card */}
            <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">{t('transporter.alreadyPartner')}</h3>
              <p className="text-gray-400 mb-6">{t('transporter.loginToAccount')}</p>
              <Button
                onClick={() => navigate('/drivers/login')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 rounded-full"
                data-testid="login-to-account-btn"
              >
                {t('transporter.loginToYourAccount')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('transporter.registerBusiness')}
            </h2>
            <p className="text-gray-400">{t('transporter.registerSubtitle')}</p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-center">
              <p className="text-red-500">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-12" data-testid="transporter-register-form">
            {/* Company Information */}
            <div className="mb-10">
              <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">1</span>
                {t('transporter.companyInfo')}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Building2 className="w-4 h-4 inline mr-2" />
                    {t('transporter.companyName')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    placeholder={t('transporter.companyName')}
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl ${errors.company_name ? 'border-red-500' : ''}`}
                    data-testid="company-name-input"
                  />
                  {errors.company_name && <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>}
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <User className="w-4 h-4 inline mr-2" />
                    {t('transporter.contactPerson')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    placeholder={t('transporter.contactPerson')}
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl ${errors.contact_person ? 'border-red-500' : ''}`}
                    data-testid="contact-person-input"
                  />
                  {errors.contact_person && <p className="text-red-500 text-sm mt-1">{errors.contact_person}</p>}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-10">
              <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">2</span>
                {t('transporter.contactInfo')}
              </h3>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Mail className="w-4 h-4 inline mr-2" />
                    {t('auth.email')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="company@example.com"
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="email-input"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Phone className="w-4 h-4 inline mr-2" />
                    {t('tripForm.phone')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+212 123 456 7890"
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl ${errors.phone ? 'border-red-500' : ''}`}
                    data-testid="phone-input"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">
                  <Globe className="w-4 h-4 inline mr-2" />
                  {t('tripForm.country')} <span className="text-red-500">*</span>
                </Label>
                <Select onValueChange={(value) => handleSelectChange('country', value)}>
                  <SelectTrigger className={`bg-black/30 border-white/10 text-white h-12 rounded-xl ${errors.country ? 'border-red-500' : ''}`} data-testid="country-select">
                    <SelectValue placeholder={t('tripForm.selectCountry')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {countries.map((country) => (
                      <SelectItem key={country} value={country} className="text-white hover:bg-amber-500/20">
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
              </div>
            </div>

            {/* Business Details */}
            <div className="mb-10">
              <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">3</span>
                {t('transporter.businessDetails')}
              </h3>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Truck className="w-4 h-4 inline mr-2" />
                    {t('transporter.fleetSize')} <span className="text-red-500">*</span>
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('fleet_size', value)}>
                    <SelectTrigger className={`bg-black/30 border-white/10 text-white h-12 rounded-xl ${errors.fleet_size ? 'border-red-500' : ''}`} data-testid="fleet-size-select">
                      <SelectValue placeholder={t('transporter.selectFleetSize')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {fleetSizeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-white hover:bg-amber-500/20">
                          {t(option.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.fleet_size && <p className="text-red-500 text-sm mt-1">{errors.fleet_size}</p>}
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    {t('transporter.experience')} <span className="text-red-500">*</span>
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('experience_years', value)}>
                    <SelectTrigger className={`bg-black/30 border-white/10 text-white h-12 rounded-xl ${errors.experience_years ? 'border-red-500' : ''}`} data-testid="experience-select">
                      <SelectValue placeholder={t('transporter.selectExperience')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {experienceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-white hover:bg-amber-500/20">
                          {t(option.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.experience_years && <p className="text-red-500 text-sm mt-1">{errors.experience_years}</p>}
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-gray-300 mb-4 block">
                  {t('transporter.servicesOffered')} <span className="text-red-500">*</span>
                </Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {servicesOptions.map((service) => {
                    const isChecked = formData.services_offered.includes(service.id);
                    return (
                      <label
                        key={service.id}
                        htmlFor={`service-${service.id}`}
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          isChecked
                            ? 'bg-amber-500/20 border-amber-500/50'
                            : 'bg-black/30 border-white/10 hover:border-white/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`service-${service.id}`}
                          checked={isChecked}
                          onChange={() => handleServiceToggle(service.id)}
                          className="w-4 h-4 accent-amber-500 bg-black/30 border-white/20 rounded"
                          data-testid={`service-${service.id}-checkbox`}
                        />
                        <span className="text-gray-300 text-sm">{t(service.labelKey)}</span>
                      </label>
                    );
                  })}
                </div>
                {errors.services_offered && <p className="text-red-500 text-sm mt-2">{errors.services_offered}</p>}
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {t('transporter.coverageAreas')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="coverage_areas"
                  value={formData.coverage_areas}
                  onChange={handleInputChange}
                  placeholder={t('transporter.coverageAreasPlaceholder')}
                  className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl ${errors.coverage_areas ? 'border-red-500' : ''}`}
                  data-testid="coverage-areas-input"
                />
                {errors.coverage_areas && <p className="text-red-500 text-sm mt-1">{errors.coverage_areas}</p>}
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-10">
              <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">4</span>
                {t('transporter.additionalInfo')}
              </h3>
              <Textarea
                name="additional_info"
                value={formData.additional_info}
                onChange={handleInputChange}
                placeholder={t('transporter.additionalInfoPlaceholder')}
                rows={4}
                className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 rounded-xl resize-none"
                data-testid="additional-info-textarea"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 rounded-full text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-70"
              data-testid="register-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('transporter.registering')}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t('common.registerYourBusiness')}
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('transporter.benefitsTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefitsData.map((benefit, index) => (
              <div
                key={benefit.title || `benefit-${index}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 transition-all duration-300 hover:border-amber-500/50 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TransporterRegisterPage;
